import { BenefitDefinition } from "../../../benefits/benefit.types";
import {
  createEnumFormatter,
  createValueFormatter,
} from "../../../components/PlanEditor/utilities/enumFormatters";
import { CellSpec } from "../../shared/cellSpecs";

/**
 * Replaces 'multipleOptions' with frontend-specific multiple option types.
 *
 * The backend uses a single 'multipleOptions' enum value; the frontend
 * distinguishes 'multipleDollarAmounts' from 'multipleSalaryMultipliers'.
 *
 * @param enumValues - Array of enum values (may include 'multipleOptions')
 * @param includeSalaryMultiplier - Whether to add 'salaryMultiplier' (default: true)
 */
export function replaceMultipleOptionsWithFrontendTypes(
  enumValues: string[],
  includeSalaryMultiplier: boolean = true,
): string[] {
  const filtered = enumValues.filter((v) => v !== "multipleOptions");

  if (includeSalaryMultiplier) {
    return [
      ...filtered,
      "multipleDollarAmounts",
      "salaryMultiplier",
      "multipleSalaryMultipliers",
    ];
  }

  return [...filtered, "multipleDollarAmounts", "multipleSalaryMultipliers"];
}

export function applyCellEnumValues(
  cell: CellSpec,
  enumValues?: string[],
  defaultValue?: unknown,
  customLabels?: Record<string, string>,
): CellSpec {
  if (!enumValues || enumValues.length === 0) {
    return cell;
  }

  const existingValidation =
    cell.validation && typeof cell.validation === "object"
      ? (cell.validation as Record<string, unknown>)
      : {};

  const newCell: CellSpec = {
    ...cell,
    cellEditorParams: {
      ...cell.cellEditorParams,
      values: enumValues,
      ...(customLabels
        ? { formatValue: createEnumFormatter(customLabels) }
        : {}),
    },
    validation: {
      ...existingValidation,
      allowedValues: enumValues,
    },
    ...(customLabels
      ? { valueFormatter: createValueFormatter(customLabels) }
      : {}),
  };

  if (defaultValue !== undefined) {
    newCell.defaultValue = defaultValue;
  }

  return newCell;
}

export function applyCellDefaultAsMinimum(
  cell: CellSpec,
  defaultValue: unknown,
): CellSpec {
  if (defaultValue === undefined || typeof defaultValue !== "number") {
    return cell;
  }

  const existingValidation =
    cell.validation && typeof cell.validation === "object"
      ? (cell.validation as Record<string, unknown>)
      : {};

  return {
    ...cell,
    cellEditorParams: {
      ...cell.cellEditorParams,
      min: defaultValue,
    },
    validation: {
      ...existingValidation,
      minimum: defaultValue,
    },
  };
}

export function createBaseBenefitDefinition(
  label: string,
  description: string,
  cell: CellSpec,
  networkDependent: boolean,
  defaultValue?: unknown,
): BenefitDefinition {
  const def: BenefitDefinition = {
    label,
    description,
    cell,
    networkDependent,
  };

  if (defaultValue !== undefined) {
    def.defaultValue = defaultValue;
  }

  return def;
}

export function createTypeDefinition(
  label: string,
  description: string,
  cell: CellSpec,
  networkDependent: boolean,
  prefix: string,
  valueSuffix: string,
  defaultValue?: unknown,
  isRequired?: boolean,
): BenefitDefinition {
  const def = createBaseBenefitDefinition(
    label,
    description,
    cell,
    networkDependent,
    defaultValue,
  );
  def.refreshesCells = [`${prefix}${valueSuffix}`];
  if (isRequired) {
    def.isRequired = true;
  }
  return def;
}

export function createValueDefinition(
  label: string,
  description: string,
  cell: CellSpec,
  networkDependent: boolean,
  nested: boolean,
  prefix: string,
  disabledValues: string[],
  defaultValue?: unknown,
  cellVariants?: BenefitDefinition["cellVariants"],
  isRequired?: boolean,
): BenefitDefinition {
  const finalCell = applyCellDefaultAsMinimum(cell, defaultValue);

  const def = createBaseBenefitDefinition(
    label,
    description,
    finalCell,
    networkDependent,
    defaultValue,
  );
  def.nested = nested;
  def.editableWhen = {
    dependsOn: `${prefix}Type`,
    disabledValues,
  };
  def.cellVariants = cellVariants;
  if (isRequired) {
    def.isRequired = true;
  }
  return def;
}
